import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useMediaQuery } from "@/lib/useMediaQuery";
import { useSyncStore } from "@/stores/syncStore";

import { findRoute } from "../routes";
import { Sidebar } from "./Sidebar";
import { SupportBanner } from "./SupportBanner";
import { TopBar } from "./TopBar";

/**
 * The application frame.
 *
 * One scrolling content region, with the navigation and the sync indicator
 * fixed around it — the content area scrolls, not the document, so a cashier
 * working down a long product list never loses sight of either.
 *
 * **The navigation changes shape, not content.** From `lg` up it is the fixed
 * rail from the designs. Below that it is an off-canvas drawer behind a
 * hamburger, because 260px of permanent rail on a 375px phone leaves 115px for
 * the actual work. The brief asks for tablets and phones as well as tills, and
 * this is the one piece of layout that decides whether the other twenty screens
 * have room to be responsive at all.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const route = findRoute(pathname);
  const [navOpen, setNavOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const start = useSyncStore((s) => s.start);

  // Started at boot (main.tsx) and never stopped: a queue must keep uploading
  // on the sign-in screen too. Starting it here as well is idempotent, and
  // keeps the shell working wherever it is mounted without that boot code —
  // in tests, for one.
  useEffect(() => {
    start();
  }, [start]);

  // Navigating closes the drawer. `NavLink` closes it on tap too; this catches
  // the routes that are reached some other way — a redirect after signing in, a
  // deep link, the back button — so the drawer is never left open over a screen
  // the merchant has already moved on from.
  useEffect(() => setNavOpen(false), [pathname]);

  // A drawer left open across a resize would become a focus trap inside a rail
  // that is now permanently on screen, with the page behind it unreachable from
  // the keyboard. Crossing into desktop closes it — the rail is there either
  // way, so nothing is lost.
  useEffect(() => {
    if (isDesktop) setNavOpen(false);
  }, [isDesktop]);

  const closeNav = useCallback(() => setNavOpen(false), []);

  return (
    <div className="flex h-screen overflow-hidden bg-canvas">
      <Sidebar open={navOpen} onClose={closeNav} />

      {/* The backdrop. Rendered only while the drawer is open and only below
          `lg`, where the drawer exists at all — at desktop width the rail
          covers nothing and a scrim over the page would be a bug. */}
      {navOpen && (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          onClick={closeNav}
          className="fixed inset-0 z-30 cursor-default bg-navy-900/50 lg:hidden"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <SupportBanner />
        <TopBar
          title={route?.label ?? "SmartSpaza"}
          {...(route?.subtitle ? { subtitle: route.subtitle } : {})}
          navOpen={navOpen}
          onToggleNav={() => setNavOpen((open) => !open)}
        />

        {/* `key` remounts on navigation so the fade-in plays per screen and any
            scroll position from the previous route is discarded. */}
        <main
          key={pathname}
          className="scroll-thin flex-1 animate-fade-in overflow-y-auto p-4 sm:p-5 lg:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
